"use client";

import React, { useState } from "react";
import "./LibraryBadges.scss";
import { Branding, Collections } from "@/types/branding";
import { useLocale, useLocalizedData } from "@/context/LocaleContext";
import { I18nData } from "@/types/i18n";

interface LibraryBadgesProps {
  branding: Branding | null;
  journalCollections?: string[];
  collectionsMap?: Collections;
  i18nData?: I18nData;
}

const MAX_VISIBLE = 2;

export default function LibraryBadges({
  branding,
  journalCollections,
  collectionsMap,
  i18nData,
}: LibraryBadgesProps) {
  const { locale } = useLocale();
  const [isExpanded, setIsExpanded] = useState(false);
  const localizedData = useLocalizedData(i18nData);

  // If no journal collections or collections map, return null
  if (
    !journalCollections ||
    !collectionsMap ||
    journalCollections.length === 0
  ) {
    return null;
  }

  const total = journalCollections.length;
  const visibleCount = isExpanded ? total : MAX_VISIBLE;
  const hiddenCount = total - MAX_VISIBLE;

  const viewLessCollectionsButtonText =
    localizedData?.view_less_collections_button_text || "View less";
  const viewAllCollectionsButtonText =
    localizedData?.view_all_collections_button_text || "View all";
  const collectionsText = localizedData?.collections || "collections";

  return (
    <div className="library-badges">
      {journalCollections.slice(0, visibleCount).map((collectionKey) => {
        const collection = collectionsMap[collectionKey];
        if (!collection) return null;
        const libraryName = collection.localized_names[locale];
        return (
          <span
            key={collectionKey}
            className="library-badge"
            style={{
              color: branding?.main_color,
              borderColor: branding?.main_color,
            }}
            title={libraryName}
            aria-label={`Available in ${libraryName}`}
          >
            {collectionKey.toUpperCase()}
          </span>
        );
      })}
      {!isExpanded && hiddenCount > 0 && (
        <button
          className="library-badge-expand"
          onClick={() => setIsExpanded(true)}
          style={{
            color: "#ffffff",
            borderColor: branding?.main_color,
            backgroundColor: branding?.main_color,
          }}
          aria-label={`${viewAllCollectionsButtonText} ${total} ${collectionsText}`}
        >
          {viewAllCollectionsButtonText}
        </button>
      )}
      {isExpanded && (
        <button
          className="library-badge-collapse"
          onClick={() => setIsExpanded(false)}
          style={{
            color: "#ffffff",
            borderColor: branding?.main_color,
            backgroundColor: branding?.main_color,
          }}
          aria-label={`${viewLessCollectionsButtonText} ${total} ${collectionsText}`}
        >
          {viewLessCollectionsButtonText}
        </button>
      )}
    </div>
  );
}
